import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Paper, Box, Grid, TextField, Button, ButtonBase } from '@material-ui/core'
import ImageIcon from '@material-ui/icons/ImageOutlined';
import Menu from '@material-ui/core/Menu'
import { MenuItem } from '@material-ui/core'

import { BookDb, updateBook } from '../redux/bookDbSlice';
import { generateImageId } from '../redux/imageDbSlice';
import { ImageData } from '../redux/cardAttributeModelIndex';
import { defaultBookCoverImage } from '../redux/preparedImages';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useImageData from '../hooks/useImageData';
import useLatelyImages from '../hooks/useLatelyImages';
import useWindowSize from '@rehooks/window-size'
import useCurrentDb from '../hooks/useCurrentDb'
import useCurrentBookDb from '../hooks/useCurrentBookDb'
import { FullSizeImageAddType, getImgSize, updateImageDbs } from '../general';
import ImageBrowserDialog from './ImageBrowserDialog'
import DrawToolDialog from '../components/DrawToolDialog';
import { ToggleBookTreeButton } from './BookTree';

const BookCoverPaper = styled(Paper)`
  position: relative;
  overflow: auto;
  padding: 0;
`
const BookCoverBodyBox = styled(Box)`
  padding: 10px;
`
const DynamicTextField = styled(TextField)`
  width: min(640px, 100%);
`
const BookImage = styled.img`
  width: min(16vw, 160px);
  height: min(16vw, 160px);
`
const MenuButton = styled(Button)`
  size: 'large';
  width: 100%;
`

type RenderBookCoverProps = {
  book?: BookDb;
  imageHandleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}
const RenderBookCover: React.FC<RenderBookCoverProps> = ({ book, imageHandleClick }) => {
  const dataUrl = useImageData(book?.coverImage?.imageId);

  return (
    <Box mt={3}>
      <ButtonBase onDoubleClick={imageHandleClick}
        disableRipple={true} disableTouchRipple={true}
        style={{ cursor: 'auto' }}
      >
        <BookImage src={dataUrl === '' ? defaultBookCoverImage : dataUrl} draggable="false" />
      </ButtonBase>
    </Box>
  );
}

const BookCover: React.FC = () => {
  const dispatch = useDispatch();
  const windowSize = useWindowSize()
  const workHeight = windowSize.innerHeight - 64
  const bookValue = useCurrentBookDb();
  const [, bookId] = useCurrentDb();
  const [book, setBook] = useState<BookDb>();
  const [notes, setNotes] = useState<string>('');
  const [imageAnchorEl, setImageAnchorEl] = useState<null | HTMLElement>(null);
  const [imageBrowserAction, setImageBrowserAction] = useState({show:false})
  const [drawToolAction, setDrawToolAction] = useState(false);
  const userId = useCurrentUserId();
  const [latelyIds, , latelyImagesId] = useLatelyImages();

  useEffect(() => {
    setBook(bookValue);
    if (bookValue) setNotes(bookValue.notes);
  }, [bookValue])

  const changeBookName = (ev: any) => {
    const newBookName = ev.target.value
    if (bookId && newBookName) {
      dispatch(updateBook({id: bookId, name: ev.target.value}))
    }
  }
  const changeNotes = (value: string) => {
    setNotes(value);
    if (bookId) dispatch(updateBook({id: bookId, notes: value}));
  }
  const imageHandleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setImageAnchorEl(event.currentTarget)
  }
  const imageHandleClose = () => {
    setImageAnchorEl(null)
  }
  const imageBrowserOpen = () => {
    setImageBrowserAction({show: true});
    imageHandleClose();
  }
  const openUserFileSelector = () => {
    imageHandleClose()
    const selector = document.getElementById('user-file-selector');
    if (!selector) return;
    selector.click();
  }
  const getFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (!ev.target.files) return;
    const file = ev.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      const dataUrlBase64 = reader.result;
      const [width, height] = getImgSize(dataUrlBase64);
      const imageId = generateImageId();
      updateImageDbs({ dispatch, imageId, dataUrlBase64, userId, latelyIds, latelyImagesId });
      changeImage({ id: imageId, width, height });
    }
  }
  const changeImage = (image?: FullSizeImageAddType) => {
    if (bookId) {
      const coverImage: ImageData | undefined = image ? {
        imageId: image.id,
        originWidth: image.width,
        originHeight: image.height,
        trimXFrom: 0,
        trimYFrom: 0,
        trimXTo: image.width,
        trimYTo: image.height
      } : undefined;

      dispatch(updateBook({ id: bookId, coverImage }));
    }
    imageHandleClose();  
  }

  if (book) {
    return (
      <>
        <BookCoverPaper style={{height: workHeight}} elevation={0} square>
          <BookCoverBodyBox>
            <Grid container>
              <Grid item xs={12}>
                <Grid container direction="row" spacing={2}>
                  <Grid item><ToggleBookTreeButton /></Grid>
                  <Grid item style={{ width: '80%' }}>
                    <DynamicTextField variant="outlined"
                      label="ブック名"
                      defaultValue={book.name}
                      onChange={changeBookName}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <RenderBookCover book={book} imageHandleClick={imageHandleClick} />
              </Grid>
              <Grid item xs={2}>
                <MenuButton aria-controls="image-menu" aria-haspopup="true" onClick={imageHandleClick} color='primary'>
                  <ImageIcon />
                  <pre> 画像を変更する</pre>
                </MenuButton>
                <Menu id="image-menu" anchorEl={imageAnchorEl} keepMounted
                  open={Boolean(imageAnchorEl)}
                  onClose={imageHandleClose}
                >
                  <MenuItem onClick={imageBrowserOpen}>画像ブラウザ...</MenuItem>
                  <MenuItem onClick={openUserFileSelector} id="user-file-selector-button">
                    <input type="file" accept="image/png, image/jpeg" id="user-file-selector" onChange={getFile} style={{display: 'none'}} />
                    ファイルから挿入...
                  </MenuItem>
                  <MenuItem onClick={() => setDrawToolAction(true)}>絵を描いて挿入...</MenuItem>
                  <MenuItem onClick={() => changeImage()}>デフォルト画像に戻す</MenuItem>
                </Menu>
              </Grid>
              <Grid item xs={12}>
                <form autoComplete="off">
                  <DynamicTextField variant="outlined" multiline minRows={6} maxRows={6}
                    value={notes} onChange={(ev) => changeNotes(ev.target.value)}
                  />
                </form>
              </Grid>
            </Grid>
          </BookCoverBodyBox>
        </BookCoverPaper>
        <ImageBrowserDialog 
          open={imageBrowserAction.show}
          onAddAttribute={(kind, image) => {
            setImageBrowserAction({show: false});
            changeImage(image);
          }}
          onClose={() => setImageBrowserAction({show: false})} 
        />
        <DrawToolDialog open={drawToolAction} onClose={() => setDrawToolAction(false)}
          onSave={(image: FullSizeImageAddType) => {
            setDrawToolAction(false);
            changeImage(image);
          }} 
        />
      </>
    );
  } else return (<></>);
}

export default BookCover;
